import {
  AssetRoutineState,
  CounterQuestionConfig,
  CounterResponseState,
  CounterState,
  CounterType,
  roundDecimals,
  RoutineState,
  WorkflowRoutineState,
} from '@gripp/shared-logic';
import { useEffect, useState } from 'react';
import { useController } from 'react-hook-form';
import { useFormContext } from '../../../../forms';
import { CounterExecutionComponent } from '../../../counter/counterExecutionComponent';
import { IntegrationManagedCounterNotice } from '../../../counter/integrationManagedCounterNotice';
import {
  RoutineQuestionExecutionForm,
  RoutineQuestionExecutionFormProps,
} from '../types';
import { themedStyleSheet } from '../../../../../themes/themedStyleSheet';

export const CounterQuestionExecutionForm: RoutineQuestionExecutionForm<
  CounterQuestionConfig,
  CounterResponseState
> = (
  props: RoutineQuestionExecutionFormProps<
    CounterQuestionConfig,
    CounterResponseState
  >
) => {
  const fieldPrefix = `responses.${props.responseIndex}`;
  const formKey = `${fieldPrefix}.count`;
  const oldValueKey = `${fieldPrefix}.oldValue`;
  const userCountKey = `${fieldPrefix}.userCount`;
  const counterTypeKey = `${fieldPrefix}.counterType`;
  const oumKey = `${fieldPrefix}.uom`;

  const { control, setValue, getValues } = useFormContext();
  const { field } = useController({
    control: control,
    name: formKey,
  });

  const [initialValue, setInitialValue] = useState(0);

  const getStateCount = (
    responseIndex: number,
    currentState: AssetRoutineState<RoutineState> | undefined
  ) => {
    const responses = (currentState?.state as WorkflowRoutineState)?.responses;

    if (responses && responseIndex < responses.length) {
      const response = responses[responseIndex] as unknown;
      return (response as CounterState)?.counterType === CounterType.Simple
        ? 0
        : (response as CounterState)?.count;
    }

    return (currentState?.state as CounterState)?.counterType ===
      CounterType.Simple
      ? 0
      : (currentState?.state as CounterState)?.count;
  };

  // `getStateCount` zeroes SIMPLE counters because manual entry starts from
  // scratch; a managed counter is showing what was last written, so it needs
  // the raw value.
  const readStateCount = (
    responseIndex: number,
    currentState: AssetRoutineState<RoutineState> | undefined
  ) => {
    const responses = (currentState?.state as WorkflowRoutineState)?.responses;

    if (responses && responseIndex < responses.length) {
      return (responses[responseIndex] as unknown as CounterState)?.count;
    }

    return (currentState?.state as CounterState)?.count;
  };

  const isIntegrationManaged = !!props.integrationManaged;
  const managedCount = isIntegrationManaged
    ? readStateCount(props.responseIndex, props.currentState)
    : undefined;

  useEffect(() => {
    const count = getStateCount(props.responseIndex, props.currentState);
    if (count !== undefined && count !== null) {
      setInitialValue(count);
    }
  }, [props.currentState]);

  useEffect(() => {
    const truncNumber = roundDecimals(
      initialValue,
      props.config.decimalPlaces ?? 0
    );
    if (props.currentState?.state) {
      const prior = getStateCount(props.responseIndex, props.currentState);
      if (prior !== undefined && prior !== null) {
        setValue(oldValueKey, truncNumber, {
          shouldDirty: false,
          shouldValidate: false,
        });
      } else {
        setValue(oldValueKey, undefined as any, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
    } else {
      setValue(oldValueKey, undefined as any, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
    setValue(counterTypeKey, props.config.counterType, {
      shouldDirty: false,
      shouldValidate: false,
    });
    setValue(oumKey, props.config.uom, {
      shouldDirty: false,
      shouldValidate: false,
    });
    if (getValues(formKey) === undefined) {
      setValue(userCountKey, undefined as any, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [initialValue]);

  // Must stay after the effect above, which blanks these on its first pass.
  useEffect(() => {
    if (!isIntegrationManaged) return;
    if (managedCount === undefined || managedCount === null) return;

    const truncNumber = roundDecimals(
      managedCount,
      props.config.decimalPlaces ?? 0
    );
    // Baseline matches the reading, so the operator is credited no change.
    setInitialValue(truncNumber);
    setValue(formKey, truncNumber, {
      shouldDirty: false,
      shouldValidate: false,
    });
    setValue(oldValueKey, truncNumber, {
      shouldDirty: false,
      shouldValidate: false,
    });
    setValue(userCountKey, 0, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [isIntegrationManaged, managedCount]);

  const handleChange = (value: number) => {
    if (value === null || Number.isNaN(value)) {
      field.onChange(undefined as any);
      setValue(userCountKey, undefined as any, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }
    const truncNumber = roundDecimals(value, props.config.decimalPlaces ?? 0);
    const baseline = roundDecimals(
      getValues(oldValueKey) ?? 0,
      props.config.decimalPlaces ?? 0
    );
    const delta = roundDecimals(
      truncNumber - baseline,
      props.config.decimalPlaces ?? 0
    );
    field.onChange(truncNumber);
    setValue(userCountKey, delta, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <>
      {props.integrationManaged && (
        <IntegrationManagedCounterNotice
          provider={props.integrationManaged.provider}
        />
      )}
      <CounterExecutionComponent
        onBlur={field.onBlur}
        instructions={''}
        initialValue={initialValue}
        onChange={handleChange}
        value={field.value}
        formKeyPrefix={fieldPrefix}
        autoFocus={false}
        decimalPlaces={
          props.config.decimal ? props.config.decimalPlaces : undefined
        }
        containerStyle={styles.container}
        inputContainerStyle={styles.inputContainerStyle}
        pillToggleContainerStyle={styles.pillToggleContainer}
        counterType={props.config.counterType}
        isIssueEditRoutine={props.isIssueEditRoutine}
        disabled={isIntegrationManaged}
      />
    </>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    backgroundColor: role.surface,
    borderRadius: 4,
  },
  inputContainerStyle: {
    marginTop: 0,
  },
  pillToggleContainer: {
    paddingTop: 0,
    marginTop: 10,
  },
}));
